/**
 * DecisionTree.ts - Decision tree navigation for DEFCON TERMINAL
 *
 * Builds and navigates the decision tree structure from scenario nodes.
 * Tracks visited nodes, branches explored, and handles conditional options.
 */

import type {
  Scenario,
  ScenarioNode,
  ChoiceNode,
  MultiSelectNode,
  TimedNode,
  ChoiceOption,
} from '@shared/types';
import { EventEmitter, type EventMap } from './EventEmitter';
import { StateManager } from './StateManager';

/**
 * Node visit record
 */
export interface NodeVisit {
  nodeId: string;
  timestamp: number;
  choiceId?: string;
  choiceIds?: string[]; // For multi-select
}

/**
 * Branch tracking information
 */
export interface BranchInfo {
  nodeId: string;
  totalOptions: number;
  visitedOptions: string[];
  availableOptions: string[];
}

/**
 * Decision tree statistics
 */
export interface TreeStats {
  totalNodes: number;
  visitedNodes: number;
  totalBranches: number;
  exploredBranches: number;
  endingsReached: number;
  totalEndings: number;
  uniqueDecisions: number;
  repeatedDecisions: number;
}

/**
 * Events emitted by DecisionTree
 */
export interface DecisionTreeEvents extends EventMap {
  'node:visit': { nodeId: string; node: ScenarioNode };
  'branch:explore': { nodeId: string; optionId: string };
  'ending:reach': { nodeId: string; isNew: boolean };
  'option:unavailable': { nodeId: string; optionId: string; reason: string };
}

/**
 * DecisionTree class - Manages decision tree navigation
 */
export class DecisionTree extends EventEmitter<DecisionTreeEvents> {
  private scenario: Scenario | null = null;
  private currentNodeId: string | null = null;
  private visitHistory: NodeVisit[] = [];
  private visitedNodes: Set<string> = new Set();
  private visitedBranches: Map<string, Set<string>> = new Map();
  private reachedEndings: Set<string> = new Set();
  private stateManager: StateManager | null = null;

  /**
   * Initialize the decision tree with a scenario
   *
   * @param scenario - Scenario to build tree from
   * @param stateManager - Optional state manager for condition evaluation
   */
  initialize(scenario: Scenario, stateManager?: StateManager): void {
    this.scenario = scenario;
    this.stateManager = stateManager ?? null;
    this.currentNodeId = scenario.startNode;
    this.visitHistory = [];
    this.visitedNodes = new Set();
    this.visitedBranches = new Map();
    this.reachedEndings = new Set();

    // Mark start node as visited
    this.markNodeVisited(scenario.startNode);
  }

  /**
   * Get the current node
   */
  getCurrentNode(): ScenarioNode | null {
    if (!this.scenario || !this.currentNodeId) {
      return null;
    }
    return this.scenario.nodes[this.currentNodeId] ?? null;
  }

  /**
   * Get the current node ID
   */
  getCurrentNodeId(): string | null {
    return this.currentNodeId;
  }

  /**
   * Get a node by ID
   */
  getNode(nodeId: string): ScenarioNode | null {
    if (!this.scenario) {
      return null;
    }
    return this.scenario.nodes[nodeId] ?? null;
  }

  /**
   * Check if a node exists
   */
  hasNode(nodeId: string): boolean {
    return this.scenario?.nodes[nodeId] !== undefined;
  }

  /**
   * Mark a node as visited
   */
  private markNodeVisited(nodeId: string, choiceId?: string, choiceIds?: string[]): void {
    this.visitedNodes.add(nodeId);

    const visit: NodeVisit = {
      nodeId,
      timestamp: Date.now(),
      choiceId,
      choiceIds,
    };
    this.visitHistory.push(visit);

    const node = this.getNode(nodeId);
    if (node) {
      this.emit('node:visit', { nodeId, node });

      // Track ending
      if (node.type === 'ending') {
        const isNew = !this.reachedEndings.has(nodeId);
        this.reachedEndings.add(nodeId);
        this.emit('ending:reach', { nodeId, isNew });
      }
    }
  }

  /**
   * Mark a branch as explored
   */
  private markBranchExplored(nodeId: string, optionId: string): void {
    if (!this.visitedBranches.has(nodeId)) {
      this.visitedBranches.set(nodeId, new Set());
    }
    this.visitedBranches.get(nodeId)!.add(optionId);

    this.emit('branch:explore', { nodeId, optionId });
  }

  /**
   * Navigate to the next node based on a choice
   *
   * @param optionId - ID of the chosen option
   * @returns The new current node or null if navigation failed
   */
  navigateToChoice(optionId: string): ScenarioNode | null {
    const currentNode = this.getCurrentNode();
    if (!currentNode || !this.currentNodeId) {
      console.warn('No current node to navigate from');
      return null;
    }

    // Handle choice-based nodes
    if (this.isChoiceBasedNode(currentNode)) {
      const option = this.findOption(currentNode, optionId);

      if (!option) {
        console.warn(`Option not found: ${optionId}`);
        return null;
      }

      // Check if option is available
      if (!this.isOptionAvailable(option)) {
        this.emit('option:unavailable', {
          nodeId: this.currentNodeId,
          optionId,
          reason: 'Condition not met',
        });
        return null;
      }

      // Mark branch as explored
      this.markBranchExplored(this.currentNodeId, optionId);

      // Navigate to next node
      this.currentNodeId = option.next;
      this.markNodeVisited(this.currentNodeId, optionId);

      return this.getCurrentNode();
    }

    // Handle narrative/consequence nodes (auto-advance)
    if (currentNode.type === 'narrative' || currentNode.type === 'consequence') {
      this.currentNodeId = currentNode.next;
      this.markNodeVisited(this.currentNodeId);
      return this.getCurrentNode();
    }

    console.warn(`Cannot navigate from node type: ${currentNode.type}`);
    return null;
  }

  /**
   * Handle multi-select node choices
   *
   * @param optionIds - Array of selected option IDs
   * @returns The new current node or null if navigation failed
   */
  navigateMultiSelect(optionIds: string[]): ScenarioNode | null {
    const currentNode = this.getCurrentNode();
    if (!currentNode || !this.currentNodeId) {
      console.warn('No current node to navigate from');
      return null;
    }

    if (currentNode.type !== 'multi-select') {
      console.warn('Current node is not a multi-select node');
      return null;
    }

    const multiNode = currentNode as MultiSelectNode;

    // Validate selection count
    const minSelections = multiNode.minSelections ?? 1;
    const maxSelections = multiNode.maxSelections ?? multiNode.options.length;

    if (optionIds.length < minSelections) {
      console.warn(`Too few selections: ${optionIds.length} < ${minSelections}`);
      return null;
    }

    if (optionIds.length > maxSelections) {
      console.warn(`Too many selections: ${optionIds.length} > ${maxSelections}`);
      return null;
    }

    // Validate all options exist and are available
    for (const optionId of optionIds) {
      const option = this.findOption(multiNode, optionId);
      if (!option) {
        console.warn(`Option not found: ${optionId}`);
        return null;
      }
      if (!this.isOptionAvailable(option)) {
        this.emit('option:unavailable', {
          nodeId: this.currentNodeId,
          optionId,
          reason: 'Condition not met',
        });
        return null;
      }
    }

    // Mark all selected branches as explored
    for (const optionId of optionIds) {
      this.markBranchExplored(this.currentNodeId, optionId);
    }

    // Navigate to the first selected option's next node
    // (Multi-select typically uses the first selection for navigation)
    const firstOption = this.findOption(multiNode, optionIds[0])!;
    this.currentNodeId = firstOption.next;
    this.markNodeVisited(this.currentNodeId, undefined, optionIds);

    return this.getCurrentNode();
  }

  /**
   * Auto-advance to the next node (for narrative/consequence nodes)
   *
   * @returns The new current node or null if can't advance
   */
  advance(): ScenarioNode | null {
    const currentNode = this.getCurrentNode();
    if (!currentNode) {
      return null;
    }

    if (currentNode.type === 'narrative' || currentNode.type === 'consequence') {
      this.currentNodeId = currentNode.next ?? null;
      if (this.currentNodeId) {
        this.markNodeVisited(this.currentNodeId);
      }
      return this.getCurrentNode();
    }

    return null;
  }

  /**
   * Check if the current node requires a choice
   */
  requiresChoice(): boolean {
    const currentNode = this.getCurrentNode();
    if (!currentNode) {
      return false;
    }
    return this.isChoiceBasedNode(currentNode);
  }

  /**
   * Check if the current node is an ending
   */
  isAtEnding(): boolean {
    const currentNode = this.getCurrentNode();
    return currentNode?.type === 'ending';
  }

  /**
   * Check if a node is a choice-based node
   */
  private isChoiceBasedNode(
    node: ScenarioNode
  ): node is ChoiceNode | MultiSelectNode | TimedNode {
    return (
      node.type === 'choice' ||
      node.type === 'multi-select' ||
      node.type === 'timed'
    );
  }

  /**
   * Find an option in a choice-based node
   */
  private findOption(
    node: ChoiceNode | MultiSelectNode | TimedNode,
    optionId: string
  ): ChoiceOption | undefined {
    return node.options.find((opt) => opt.id === optionId);
  }

  /**
   * Check if an option is available based on conditions
   */
  isOptionAvailable(option: ChoiceOption): boolean {
    // Hidden options are not available unless discovered
    if (option.hidden) {
      return false;
    }

    // Check condition if present
    if (option.condition && this.stateManager) {
      return this.stateManager.evaluateCondition(option.condition);
    }

    return true;
  }

  /**
   * Get available options for the current node
   */
  getAvailableOptions(): ChoiceOption[] {
    const currentNode = this.getCurrentNode();
    if (!currentNode || !this.isChoiceBasedNode(currentNode)) {
      return [];
    }

    return currentNode.options.filter((opt) => this.isOptionAvailable(opt));
  }

  /**
   * Get all options for the current node (including unavailable)
   */
  getAllOptions(): ChoiceOption[] {
    const currentNode = this.getCurrentNode();
    if (!currentNode || !this.isChoiceBasedNode(currentNode)) {
      return [];
    }

    return currentNode.options;
  }

  /**
   * Get visit history
   */
  getVisitHistory(): NodeVisit[] {
    return [...this.visitHistory];
  }

  /**
   * Get visited nodes
   */
  getVisitedNodes(): string[] {
    return Array.from(this.visitedNodes);
  }

  /**
   * Check if a node has been visited
   */
  hasVisited(nodeId: string): boolean {
    return this.visitedNodes.has(nodeId);
  }

  /**
   * Get branch information for a node
   */
  getBranchInfo(nodeId: string): BranchInfo | null {
    const node = this.getNode(nodeId);
    if (!node || !this.isChoiceBasedNode(node)) {
      return null;
    }

    const visited = this.visitedBranches.get(nodeId) ?? new Set();
    const allOptions = node.options.map((opt) => opt.id);
    const availableOptions = node.options
      .filter((opt) => this.isOptionAvailable(opt))
      .map((opt) => opt.id);

    return {
      nodeId,
      totalOptions: node.options.length,
      visitedOptions: Array.from(visited),
      availableOptions,
    };
  }

  /**
   * Calculate tree statistics
   */
  getStats(): TreeStats {
    if (!this.scenario) {
      return {
        totalNodes: 0,
        visitedNodes: 0,
        totalBranches: 0,
        exploredBranches: 0,
        endingsReached: 0,
        totalEndings: 0,
        uniqueDecisions: 0,
        repeatedDecisions: 0,
      };
    }

    const nodes = Object.values(this.scenario.nodes);
    const totalNodes = nodes.length;
    const totalEndings = nodes.filter((n) => n.type === 'ending').length;

    let totalBranches = 0;
    let exploredBranches = 0;

    for (const [nodeId, node] of Object.entries(this.scenario.nodes)) {
      if (
        node.type === 'choice' ||
        node.type === 'multi-select' ||
        node.type === 'timed'
      ) {
        totalBranches += node.options.length;
        const visited = this.visitedBranches.get(nodeId);
        if (visited) {
          exploredBranches += visited.size;
        }
      }
    }

    // Count unique vs repeated decisions
    const decisionCounts = new Map<string, number>();
    for (const visit of this.visitHistory) {
      if (visit.choiceId) {
        const key = `${visit.nodeId}:${visit.choiceId}`;
        decisionCounts.set(key, (decisionCounts.get(key) ?? 0) + 1);
      }
    }

    let uniqueDecisions = 0;
    let repeatedDecisions = 0;
    for (const count of decisionCounts.values()) {
      uniqueDecisions += 1;
      if (count > 1) {
        repeatedDecisions += count - 1;
      }
    }

    return {
      totalNodes,
      visitedNodes: this.visitedNodes.size,
      totalBranches,
      exploredBranches,
      endingsReached: this.reachedEndings.size,
      totalEndings,
      uniqueDecisions,
      repeatedDecisions,
    };
  }

  /**
   * Calculate branches explored percentage
   */
  getBranchesExploredPercentage(): number {
    const stats = this.getStats();
    if (stats.totalBranches === 0) {
      return 100;
    }
    return Math.round((stats.exploredBranches / stats.totalBranches) * 100);
  }

  /**
   * Get the path taken so far (node IDs in order)
   */
  getPath(): string[] {
    return this.visitHistory.map((v) => v.nodeId);
  }

  /**
   * Get decision count (choices made)
   */
  getDecisionCount(): number {
    return this.visitHistory.filter((v) => v.choiceId || v.choiceIds).length;
  }

  /**
   * Reset to start of scenario
   */
  reset(): void {
    if (!this.scenario) {
      return;
    }

    this.currentNodeId = this.scenario.startNode;
    this.visitHistory = [];
    this.visitedNodes = new Set();
    this.visitedBranches = new Map();
    this.reachedEndings = new Set();

    this.markNodeVisited(this.scenario.startNode);
  }

  /**
   * Export state for serialization
   */
  toJSON(): {
    currentNodeId: string | null;
    visitHistory: NodeVisit[];
    visitedNodes: string[];
    visitedBranches: Record<string, string[]>;
    reachedEndings: string[];
  } {
    const visitedBranches: Record<string, string[]> = {};
    this.visitedBranches.forEach((options, nodeId) => {
      visitedBranches[nodeId] = Array.from(options);
    });

    return {
      currentNodeId: this.currentNodeId,
      visitHistory: [...this.visitHistory],
      visitedNodes: Array.from(this.visitedNodes),
      visitedBranches,
      reachedEndings: Array.from(this.reachedEndings),
    };
  }

  /**
   * Import state from serialized data
   */
  fromJSON(data: {
    currentNodeId: string | null;
    visitHistory?: NodeVisit[];
    visitedNodes?: string[];
    visitedBranches?: Record<string, string[]>;
    reachedEndings?: string[];
  }): void {
    this.currentNodeId = data.currentNodeId;
    this.visitHistory = data.visitHistory ?? [];
    this.visitedNodes = new Set(data.visitedNodes ?? []);
    this.reachedEndings = new Set(data.reachedEndings ?? []);

    this.visitedBranches = new Map();
    if (data.visitedBranches) {
      for (const [nodeId, options] of Object.entries(data.visitedBranches)) {
        this.visitedBranches.set(nodeId, new Set(options));
      }
    }
  }
}
